import { Component, DestroyRef, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  BokkzWageDataService,
  Dienstverband,
  Periode,
  WerkdagenEvenWeek,
  WerkdagenOnevenWeek,
  WerknemerEntity,
  Werkrooster
} from '@snelstart-ventures/fiscaal/domain-logic';
import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { DetailsConfig, SharedUiDetailsModule } from '@snelstart/ui-details';
import { SelectFormControlItem, SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import { SharedUiButtonModule } from '@snelstart/ui-button';
import { SharedUiTabModule } from '@snelstart/ui-tab';
import { SharedUiDialogModule } from '@snelstart/ui-dialog';
import { SharedUiIconModule } from '@snelstart/ui-icon';
import { debounceTime, filter, merge, Observable, shareReplay, Subscription, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

type PeriodeFormGroup = FormGroup<{
  begindatum: FormControl<Date | null>;
  einddatum: FormControl<Date | null>;
}>;

type WerkroosterFormGroup = FormGroup<{
  periode: PeriodeFormGroup;
  aantalUrenPerWeek: FormControl<number | null>;
  normUren: FormControl<number | null>;
  werkdagenEvenWeek: FormControl<WerkdagenEvenWeek[] | null>;
  werkdagenOnevenWeek: FormControl<WerkdagenOnevenWeek[] | null>;
}>;

type LijstFormGroup = FormGroup<{
  roosterGegevens: FormArray<WerkroosterFormGroup>;
}>;

@Component({
  selector: 'snelstart-venture-fiscaal-rooster',
  standalone: true,
  imports: [
    CommonModule,
    SharedUiDetailsModule,
    SharedUiFormControlsModule,
    SharedUiButtonModule,
    SharedUiTabModule,
    SharedUiDialogModule,
    SharedUiIconModule
  ],
  templateUrl: './rooster.component.html',
  styleUrl: './rooster.component.scss'
})
export class RoosterComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  @Input() selectedTabDv = 0;
  private readonly destroyRef = inject(DestroyRef);
  private readonly dataService = inject(BokkzWageDataService);
  private werknemerEntity = {} as WerknemerEntity;

  protected detailsConfig = DetailsConfig.create({
    hideArrowButtons: false
  });

  public lijstFg: LijstFormGroup = new FormGroup({
    roosterGegevens: new FormArray<WerkroosterFormGroup>([])
  });
  private recalculationSubscription?: Subscription;

  public optionsJaNee: SelectFormControlItem<boolean>[] = [
    { value: true, label: 'Ja' },
    { value: false, label: 'Nee' }
  ];

  readonly werknemerEntity$: Observable<WerknemerEntity> =
    this.dataService.werknemerEntity$.pipe(
      filter((data): data is WerknemerEntity => !!data?.id),
      tap((data) => {
        this.werknemerEntity = data;
        if (data.werknemerGegevens?.dienstverbanden?.at(0)) {
          this.updateForm(data.werknemerGegevens.dienstverbanden[0].werkrooster ?? []);
        }
      }),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({ bufferSize: 1, refCount: true })
    );

  ngOnInit(): void {
    this.bindRecalculationHandlers();
  }

  ngOnDestroy(): void {
    this.recalculationSubscription?.unsubscribe();
    this.saveForm();
  }

  get roosterGegevens(): FormArray<WerkroosterFormGroup> {
    return this.lijstFg.controls.roosterGegevens;
  }

  addRij(): void {
    const length = this.roosterGegevens.length;
    let newDate = new Date("2026-12-31");
    if (length > 0) {
      const lastRow = this.roosterGegevens.at(length - 1);
      const laatsteEinddatum = new Date(lastRow.controls.periode.controls.einddatum.value!);
      newDate = new Date(laatsteEinddatum);
      newDate.setDate(laatsteEinddatum.getDate() + 1);
    }
    const nieuweRoosterGegevens = {
      periode: {
        begindatum: newDate
      },
      normUren: this.roosterGegevens.at(length - 1).controls.normUren.value,
      aantalUrenPerWeek: this.roosterGegevens.at(length - 1).controls.aantalUrenPerWeek.value
    } as Werkrooster;
    this.roosterGegevens.push(this.createFormGroup(nieuweRoosterGegevens));
    this.bindRecalculationHandlers();
    this.saveForm();
  }

  verwijderRijRooster(index: number): void {
    if (index > 0) {
      this.roosterGegevens.at(index - 1).controls.periode.patchValue({ einddatum: undefined });
    }
    this.roosterGegevens.removeAt(index);
    this.roosterGegevens.updateValueAndValidity();
    this.bindRecalculationHandlers();
    this.saveForm();
  }

  private createFormGroup(item?: Werkrooster): WerkroosterFormGroup {
    return new FormGroup({
      periode: this.createPeriodeFormGroup(item?.periode),
      aantalUrenPerWeek: new FormControl<number | null>(item?.aantalUrenPerWeek ?? null),
      normUren: new FormControl<number | null>(item?.normUren ?? null),
      werkdagenEvenWeek: new FormControl<WerkdagenEvenWeek[] | null>(item?.werkdagenEvenWeek ?? null),
      werkdagenOnevenWeek: new FormControl<WerkdagenOnevenWeek[] | null>(item?.werkdagenOnevenWeek ?? null),
    });
  }

  private createPeriodeFormGroup(periode?: Periode): PeriodeFormGroup {
    return new FormGroup({
      begindatum: new FormControl<Date | null>(periode?.begindatum ?? null),
      einddatum: new FormControl<Date | null>(periode?.einddatum ?? null),
    });
  }

  private updateForm(werkrooster: Werkrooster[]): void {
    const lijst = werkrooster || [];
    const formArray = this.roosterGegevens;

    while (formArray.length > lijst.length) {
      formArray.removeAt(formArray.length - 1);
    }

    for (let i = 0; i < lijst.length; i++) {
      if (i < formArray.length) {
        formArray.at(i).patchValue(lijst[i], { emitEvent: false });
      } else {
        formArray.push(this.createFormGroup(lijst[i]));
      }
    }

    this.bindRecalculationHandlers();
  }

  private bindRecalculationHandlers(): void {
    this.recalculationSubscription?.unsubscribe();

    const einddatumStreams = this.roosterGegevens.controls.map(control =>
      control.controls.periode.controls.einddatum.valueChanges
    );
    const aantalUrenPerWeekStreams = this.roosterGegevens.controls.map(control =>
      control.controls.aantalUrenPerWeek.valueChanges
    );

    const recalcStreams = [...einddatumStreams, ...aantalUrenPerWeekStreams];
    if (!recalcStreams.length) {
      this.recalculationSubscription = undefined;
      return;
    }

    this.recalculationSubscription = merge(...recalcStreams)
      .pipe(debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.saveForm();
        this.dataService
          .calculateLatestWerknemerEntity()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe();
      });
  }

  public saveForm(): void {
    const werkrooster = this.lijstFg.getRawValue().roosterGegevens;

    this.dataService.patchWerknemerEntity((current) => {
      const baseEntity = current?.werknemerGegevens ? current : this.werknemerEntity;
      const eersteDienstverband = baseEntity.werknemerGegevens?.dienstverbanden?.at(0);

      if (!baseEntity.werknemerGegevens || !eersteDienstverband) {
        return baseEntity;
      }

      const dienstverband = {
        ...eersteDienstverband,
        dga: false,
        werkrooster,
      } as Dienstverband;

      const nextWerknemer = {
        ...baseEntity,
        werknemerGegevens: {
          ...baseEntity.werknemerGegevens,
          dienstverbanden: [dienstverband]
        }
      } as WerknemerEntity;

      this.werknemerEntity = nextWerknemer;
      return nextWerknemer;
    });
  }

  public checkEinddatum(): boolean {
    const length = this.roosterGegevens.length;
    const waarde = this.roosterGegevens.at(length - 1).controls.periode.controls.einddatum.value;
    return !!waarde;
  }

}
